import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { pendingPaymentQuery } from '@amfam/billing/pending-payment/data-access';
import { BillingRegistrationSelectors } from '@amfam/billing/registration/data-access';
import { BillingPaymentPaths } from '@amfam/shared/models';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { LoadingSpinnerService } from '@amfam/ui-kit';

// Store
import * as fromRoot from '../../core/store';

@Component({
  templateUrl: './bill-account-register-wrapper.component.html',
  styleUrls: ['./bill-account-register-wrapper.component.scss'],
  standalone: false
})
export class BillAccountRegisterWrapperComponent implements OnInit, OnDestroy {
  pendingRegistrations: any[] = [];
  current: any;
  index = 0;
  acceptTerms = true;
  public showError = false;
  private pendingPayments: any[] = [];
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store<fromRoot.RootState>,
    private feature: FeatureFlagService,
    public spinner: LoadingSpinnerService
  ) {}

  ngOnInit() {
    combineLatest(
      this.store.select(pendingPaymentQuery.selectPendingPayments),
      this.store.select(BillingRegistrationSelectors.selectPendingRegistrations),
      (pendingPayments, pendingRegistrations) => ({
        pendingPayments: [...pendingPayments],
        pendingRegistrations: [...pendingRegistrations]
      })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        this.pendingRegistrations = state.pendingRegistrations;
        this.pendingPayments = state.pendingPayments;
        // Set the first list element to a current account
        if (this.pendingRegistrations && this.pendingRegistrations.length) {
          this.current = this.pendingRegistrations[0];
        }
      });
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  onRegistrationSuccess() {
    this.showError = false;
    const isMultiRegister = this.pendingRegistrations.length > 1 ? true : false;
    const hasPendingPayments = this.pendingPayments.length > 0 ? true : false;

    // AS: If we are redirecting make sure the spinner is Off.
    this.spinner.stop();

    // Check if there is more than one registration - this determines where to route to when done
    if (isMultiRegister) {
      // TO disable T & C in Bill Account Register Component.
      this.acceptTerms = false;
      // Is a Multi-Register. Check to see if we're done with all the registrations.
      if (this.pendingRegistrations.length === this.index + 1) {
        // Done with all the registrations.
        if (hasPendingPayments) {
          // Has pending payments, route to payments/schedule.
          // ONE TIME PAYMENT ENTRYPOINT
          if (this.feature.isEnabled('simplified_payment')) {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
              })
            );
          } else {
            this.store
              .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
              .pipe(take(1))
              .subscribe(billAccount => {
                if (billAccount.length === 1) {
                  this.store.dispatch(
                    fromRouterActions.Go({
                      path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
                    })
                  );
                } else {
                  this.store.dispatch(
                    fromRouterActions.Go({
                      path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
                    })
                  );
                }
              });
          }
        } else {
          // No Pending payments, was only a multi-register. Route to section page.
          this.store.dispatch(
            fromRouterActions.Go({
              path: [BillingPaymentPaths.CONFIRMATION_PATH]
            })
          );
        }
      } else {
        // Not done with registrations. Go to the next one.
        this.current = this.pendingRegistrations[++this.index];
      }
    } else {
      // Not a multi-register.
      if (hasPendingPayments) {
        // Single registration with pendign payments. Go to payments/schedule
        // ONE TIME PAYMENT ENTRYPOINT
        if (this.feature.isEnabled('simplified_payment')) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
            })
          );
        } else {
          this.store
            .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
            .pipe(take(1))
            .subscribe(billAccount => {
              if (billAccount.length === 1) {
                this.store.dispatch(
                  fromRouterActions.Go({
                    path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
                  })
                );
              } else {
                this.store.dispatch(
                  fromRouterActions.Go({
                    path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
                  })
                );
              }
            });
        }
      } else {
        // Single registration, go to the confirmation page
        this.store.dispatch(
          fromRouterActions.Go({
            path: [BillingPaymentPaths.CONFIRMATION_PATH]
          })
        );
      }
    }
  }

  onRegistrationError() {
    this.showError = true;
  }
}
